import { Link } from 'react-router-dom'
import { Compass, LogOut, MessageSquare, Settings } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTour } from '@/components/tour/tour-context'
import { WELCOME_TOUR_ID } from '@/lib/tours/welcome'
import { useAuthStore } from '@/stores/useAuthStore'
import { useFeedbackStore } from '@/stores/useFeedbackStore'
import { cn } from '@/lib/utils'

interface AccountMenuProps {
  /** `row` shows the name beside the avatar (sidebar); `icon` is the avatar alone (mobile header). */
  variant: 'row' | 'icon'
}

function initials(name: string | undefined): string {
  if (!name) return 'U'
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

export function AccountMenu({ variant }: AccountMenuProps) {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const { startTour } = useTour()
  const openFeedback = useFeedbackStore((state) => state.openFeedback)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          data-testid={variant === 'row' ? 'sidebar-user-menu-trigger' : 'user-menu-trigger'}
          data-tour="account-menu-trigger"
          className={cn(
            'hover:bg-sidebar-accent/60 flex items-center gap-2.5 rounded-xl text-sm transition-colors',
            variant === 'row' ? 'h-12 w-full px-2' : 'size-11 justify-center rounded-full'
          )}
        >
          <Avatar className="size-[30px]">
            <AvatarImage src={user?.avatar} alt={user?.name ?? 'User'} />
            <AvatarFallback className="text-xs font-semibold">
              {initials(user?.name)}
            </AvatarFallback>
          </Avatar>
          {variant === 'row' && (
            <span className="min-w-0 flex-1 truncate text-left">{user?.name ?? 'Account'}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={variant === 'row' ? 'start' : 'end'} className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{user?.name ?? 'User'}</p>
            <p className="text-muted-foreground text-xs">{user?.email ?? ''}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/settings" data-testid="account-menu-settings">
              <Settings className="mr-2 size-4" />
              Settings
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => startTour(WELCOME_TOUR_ID)} data-testid="take-a-tour">
            <Compass className="mr-2 size-4" />
            Take a tour
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openFeedback()} data-testid="account-menu-feedback">
            <MessageSquare className="mr-2 size-4" />
            Feedback
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} variant="destructive" data-testid="logout-button">
          <LogOut className="mr-2 size-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
